<template>
  <ContentModal v-model="show" title="User Lookup" size="2xl">
    <form class="lookup-form d-flex gap-2 mb-3" @submit.prevent="lookupUsers">
      <input v-model="search" class="form-control" type="search" placeholder="Email address, user ID, or mobile number" autocomplete="off">
      <button type="submit" class="btn btn-primary mb-0" :disabled="loading || !search.trim()">
        <span v-if="loading" class="spinner-border spinner-border-sm" role="status"></span>
        <i v-else class="material-icons text-sm">search</i>
        <span class="visually-hidden">Search</span>
      </button>
    </form>

    <div v-if="error" class="alert alert-danger py-2" role="alert">{{ error }}</div>
    <div v-else-if="searched && !loading && !rows.length" class="text-center text-secondary py-4">No matching people found.</div>
    <BaseTable
      v-else-if="rows.length"
      heading="Matching People"
      :rows="rows"
      :columns="columns"
      idKey="person_id"
      :searchable="false"
      :selectable="false"
      :sortable="false"
    >
      <template #col-person_id="{ row }"><span class="font-monospace">{{ row.person_id }}</span></template>
      <template #col-name="{ row }"><strong>{{ row.firstname }} {{ row.lastname }}</strong></template>
      <template #col-sms_enabled="{ row }"><span :class="row.sms_enabled ? 'text-success' : 'text-secondary'">{{ row.sms_enabled ? 'Yes' : 'No' }}</span></template>
      <template #col-proxy_email="{ row }">
        <span v-if="row.proxy_email">{{ row.proxy_email }}<small v-if="row.proxy_email_enabled" class="d-block text-success">Enabled</small></span>
        <span v-else class="text-secondary">-</span>
      </template>
      <template #col-last_login="{ row }">{{ formatDate(row.last_login) }}</template>
      <template #col-organizations="{ row }"><div v-if="row.organizations.length" class="membership-list"><span v-for="organization in row.organizations" :key="`organization-${organization.id}`" class="membership-item"><strong>{{ organization.name }}</strong> <small>(#{{ organization.id }})</small></span></div><span v-else class="text-secondary">-</span></template>
      <template #col-locations="{ row }"><div v-if="row.locations.length" class="membership-list"><span v-for="location in row.locations" :key="`location-${location.id}`" class="membership-item"><strong>{{ location.name }}</strong> <small>(#{{ location.id }})</small></span></div><span v-else class="text-secondary">-</span></template>
    </BaseTable>
    <p v-else class="text-secondary small mb-0">Search by a user ID, email address, or mobile number.</p>
  </ContentModal>
</template>

<script setup>
import { computed, ref } from 'vue'
import ContentModal from '@/components/Modal/ContentModal.vue'
import BaseTable from '@/components/Table/Table.vue'
import api from '@/config/api'

const props = defineProps({ modelValue: Boolean })
const emit = defineEmits(['update:modelValue'])
const show = computed({ get: () => props.modelValue, set: (value) => emit('update:modelValue', value) })
const search = ref('')
const rows = ref([])
const loading = ref(false)
const searched = ref(false)
const error = ref('')
const columns = [
  { key: 'person_id', label: 'ID' },
  { key: 'name', label: 'NAME' },
  { key: 'email', label: 'EMAIL' },
  { key: 'mobile_phone', label: 'MOBILE' },
  { key: 'sms_enabled', label: 'SMS ENABLED' },
  { key: 'proxy_email', label: 'PROXY EMAIL' },
  { key: 'last_login', label: 'LAST LOGIN' },
  { key: 'organizations', label: 'ORGANIZATIONS' },
  { key: 'locations', label: 'LOCATIONS' },
]

async function lookupUsers() {
  const value = search.value.trim()
  if (!value) return
  loading.value = true
  searched.value = true
  error.value = ''
  try {
    const response = await api.get('/admin/user-lookup', { params: { search: value } })
    rows.value = response.data?.data || []
  } catch (exception) {
    rows.value = []
    error.value = exception.response?.data?.message || 'Unable to complete the user lookup.'
  } finally {
    loading.value = false
  }
}

function formatDate(value) {
  if (!value) return '-'
  const date = new Date(value)
  return Number.isNaN(date.getTime()) ? value : date.toLocaleString()
}
</script>

<style scoped>
.lookup-form .form-control { min-width: 0; }
.lookup-form .btn { flex: 0 0 auto; }
.membership-list { min-width: 180px; }
.membership-item { display: block; white-space: nowrap; }
.membership-item small { color: #64748b; }
:deep(.table-responsive) { max-height: 52vh; overflow: auto; }
@media (max-width: 575.98px) { .lookup-form { flex-direction: column; } .lookup-form .btn { width: 100%; } }
</style>
